import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';

const AuthContext = createContext(null);

// Usernames que são Superadmin fixos
const SUPERADMIN_USERNAMES = ['admin', 'maycon'];

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children, initialUser, onLogout }) => {
  const [user, setUser] = useState(initialUser);

  // Update user when initialUser changes
  useEffect(() => {
    setUser(initialUser);
  }, [initialUser]);

  // Check if user is Superadmin (username fixo: admin ou maycon)
  const isSuperAdmin = useCallback(() => {
    if (!user) return false;
    const username = (user.username || '').toLowerCase();
    return SUPERADMIN_USERNAMES.includes(username) || user.is_superadmin;
  }, [user]);

  // Check if user is Admin Total (superadmin OR is_admin flag)
  const isAdminTotal = useCallback(() => {
    if (!user) return false;
    return isSuperAdmin() || user.is_admin || user.is_admin_total;
  }, [user, isSuperAdmin]);

  // Legacy function for backwards compatibility
  const isAdmin = useCallback(() => {
    return isAdminTotal();
  }, [isAdminTotal]);

  // Check if user has a specific permission
  const hasPermission = useCallback((permission) => {
    if (!user) return false;
    
    // Hierarquia de poder:
    // 1. Superadmin (username fixo) - poder absoluto
    if (isSuperAdmin()) return true;
    
    // 2. Admin Total (is_admin=true) - poder total
    if (user.is_admin) return true;
    
    // 3. Verificar permissão específica
    return user.permissions?.[permission] || false;
  }, [user, isSuperAdmin]);

  // Check if user can view a module
  const canView = useCallback((module) => {
    return hasPermission(`${module}_view`);
  }, [hasPermission]);

  // Check if user can edit a module
  const canEdit = useCallback((module) => {
    return hasPermission(`${module}_edit`);
  }, [hasPermission]);

  // Check if user can manage users (permissão específica OU admin)
  const canManageUsers = useCallback(() => {
    return hasPermission('users_manage');
  }, [hasPermission]);

  // Refresh user data from server
  const refreshUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      const updatedUser = response.data;
      localStorage.setItem('scfv_user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      return updatedUser;
    } catch (error) {
      console.error('Error refreshing user:', error);
      return null;
    }
  }, []);

  const value = {
    user,
    setUser,
    hasPermission,
    canView,
    canEdit,
    isAdmin,        // Legacy - equivalente a isAdminTotal
    isSuperAdmin,   // Username fixo (admin, maycon)
    isAdminTotal,   // Superadmin OU is_admin
    canManageUsers, // Permissão específica
    refreshUser,
    onLogout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
